import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, ModalLayout, Textfield } from '@aimsos/ui';

import { useWorkspaceSaveFlow } from '../hooks';
import WorkspaceLogoDropzone from './WorkspaceLogoDropzone';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const LOGO_ACCEPT = 'image/png,image/jpeg,image/jpg,image/svg+xml';

const simpleEmailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());

/**
 * Create workspace — POST /v1/workspace/create (metadata + logo fields when present).
 * With logo: first POST /v1/file/upload-url + PUT to S3; `path` on create and on /photo = `s3_path` from that response.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {(workspaceId: string | null) => void} [props.onCreated] — called after success with new id when API returns it
 */
const CreateWorkspaceModal = ({ isOpen, onClose, onCreated }) => {
  const { t } = useTranslation('workspaces');
  const { executeCreate: runCreateWorkspaceApi } = useWorkspaceSaveFlow(t);
  const titleId = useId();
  const fileInputRef = useRef(null);

  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [address, setAddress] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoError, setLogoError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const reset = useCallback(() => {
    setName('');
    setOwnerEmail('');
    setContactName('');
    setAddress('');
    setLogoFile(null);
    setLogoError('');
    setSubmitted(false);
    setSubmitting(false);
    setSubmitError(null);
    setDragActive(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  const validateFile = useCallback(
    (file) => {
      if (!file) {
        setLogoFile(null);
        setLogoError('');
        return;
      }
      const type = (file.type || '').toLowerCase();
      const okType =
        type === 'image/png' ||
        type === 'image/jpeg' ||
        type === 'image/jpg' ||
        type === 'image/svg+xml';
      if (!okType) {
        setLogoError(t('list.createModal.logoType'));
        return;
      }
      if (file.size > MAX_LOGO_BYTES) {
        setLogoError(t('list.createModal.logoTooLarge'));
        return;
      }
      setLogoError('');
      setLogoFile(file);
    },
    [t],
  );

  const nameError = submitted && !name.trim() ? t('list.createModal.nameRequired') : '';
  const ownerError =
    submitted && !ownerEmail.trim()
      ? t('list.createModal.ownerRequired')
      : submitted && ownerEmail.trim() && !simpleEmailValid(ownerEmail)
        ? t('list.createModal.ownerInvalid')
        : '';
  const contactError = submitted && !contactName.trim() ? t('list.createModal.contactRequired') : '';

  const handleDismiss = useCallback(() => {
    if (submitting) return;
    reset();
    onClose?.();
  }, [onClose, reset, submitting]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleDismiss]);

  const handleSubmit = useCallback(async () => {
    setSubmitted(true);
    setSubmitError(null);
    if (!name.trim() || !ownerEmail.trim() || !simpleEmailValid(ownerEmail) || !contactName.trim()) {
      return;
    }
    if (logoError) return;

    setSubmitting(true);
    try {
      const trimmedName = name.trim();
      const slug = trimmedName.toLowerCase().replace(/\s+/g, '-');

      const result = await runCreateWorkspaceApi({
        trimmedName,
        slug,
        ownerEmail: ownerEmail.trim(),
        contactName: contactName.trim(),
        address: address.trim(),
        logoFile,
      });

      if (result.type === 'photo_error') {
        onCreated?.(result.workspaceId);
        setSubmitError(result.message);
        return;
      }

      onCreated?.(result.workspaceId);
      reset();
      onClose?.();
    } catch (err) {
      setSubmitError(err?.message ?? t('list.createModal.submitError'));
    } finally {
      setSubmitting(false);
    }
  }, [
    name,
    ownerEmail,
    contactName,
    address,
    logoFile,
    logoError,
    onClose,
    onCreated,
    reset,
    runCreateWorkspaceApi,
    t,
  ]);

  const onFileInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      validateFile(file ?? null);
    },
    [validateFile],
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files?.[0];
      validateFile(file ?? null);
    },
    [validateFile],
  );

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleDismiss}
      title={t('list.createModal.title')}
      subtitle={t('list.createModal.subtitle')}
      titleId={titleId}
      closeAriaLabel={t('list.createModal.closeAria')}
      maxWidthClassName="max-w-xl"
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className="flex flex-col gap-4">
        <Textfield
          label={t('list.createModal.nameLabel')}
          placeholder={t('list.createModal.namePlaceholder')}
          value={name}
          onChange={setName}
          state={nameError ? 'error' : 'default'}
          supportingText={nameError || undefined}
        />

        <Textfield
          label={t('list.createModal.ownerLabel')}
          placeholder={t('list.createModal.ownerPlaceholder')}
          value={ownerEmail}
          onChange={setOwnerEmail}
          type="email"
          autoComplete="email"
          state={ownerError ? 'error' : 'default'}
          supportingText={ownerError || undefined}
        />

        <Textfield
          label={t('list.createModal.contactLabel')}
          placeholder={t('list.createModal.contactPlaceholder')}
          value={contactName}
          onChange={setContactName}
          state={contactError ? 'error' : 'default'}
          supportingText={contactError || undefined}
        />

        <Textfield
          label={t('list.createModal.addressLabel')}
          placeholder={t('list.createModal.addressPlaceholder')}
          value={address}
          onChange={setAddress}
        />

        <div className="flex flex-col gap-1.5">
          <p className="text-[14px] font-bold leading-tight text-content">{t('list.createModal.logoLabel')}</p>
          <WorkspaceLogoDropzone
            inputId="create-workspace-logo"
            fileInputRef={fileInputRef}
            accept={LOGO_ACCEPT}
            logoFile={logoFile}
            existingImageUrl={null}
            workspaceName={name}
            dragActive={dragActive}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              if (!e.currentTarget.contains(e.relatedTarget)) setDragActive(false);
            }}
            onDrop={onDrop}
            onFileInputChange={onFileInputChange}
            hasError={Boolean(logoError)}
            hintBold={t('list.createModal.logoHintBold')}
            hintFormats={t('list.createModal.logoHintFormats')}
            altPreview={t('list.createModal.logoPreviewAlt')}
          />
          {logoError ? <p className="px-1 text-xs text-red-700 dark:text-red-300">{logoError}</p> : null}
        </div>

        {submitError ? (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            {submitError}
          </p>
        ) : null}
        </div>
      </div>

      <div className="shrink-0 bg-surface-muted/20 px-5 py-4">
        <div className="flex justify-end gap-2">
          <Button variant="secondary" type="button" size="md" onClick={handleDismiss} disabled={submitting}>
            {t('list.createModal.cancel')}
          </Button>
          <Button variant="primary" type="button" size="md" loading={submitting} onClick={handleSubmit}>
            {submitting ? t('list.createModal.creating') : t('list.createModal.create')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
};

export default CreateWorkspaceModal;
